// src/components/Modelo/ModeloHero/ModeloHero.jsx
import React, { Suspense, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import TagManager from 'react-gtm-module';
import ModeloCotizacion from '../ModeloCotizacion/ModeloCotizacion';
import ModeloFicha from '../ModeloFicha/ModeloFicha';
import ModelPreview from '../Productos/ModelPreview';
import styles from './ModeloHero.module.scss';
import BackgroundStroke from '../BackgroundStroke/BackgroundStroke';

const LazyImage = React.lazy(() => import('../LazyImage'));

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { when: 'beforeChildren', staggerChildren: 0.2 }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const imageVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { type: 'spring', stiffness: 120, damping: 20 }
  }
};

const buttonsContainerVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: 'spring', bounce: 0.5, duration: 0.6 }
  }
};

const ModeloHero = ({ modelo, onOpenCotizador }) => {
  const navigate = useNavigate();
  const { nombre, serie, linea, imagenCardUrl } = modelo;
  const serieNombre = serie?.nombre || 'Sin serie';
  const lineaNombre = linea?.nombre || 'Sin línea';

  const [isPreviewOpen, setPreviewOpen] = useState(false);
  const [canClick, setCanClick] = useState(false);

  // Animaciones controladas
  const infoControls = useAnimation();
  const buttonsControls = useAnimation();

  useEffect(() => {
    infoControls
      .start('visible')
      .then(() => buttonsControls.start('visible'));
  }, [infoControls, buttonsControls]);

  useEffect(() => {
    const mql = window.matchMedia('(pointer: fine)');
    setCanClick(mql.matches);
    const handler = (e) => setCanClick(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const handleClickSerie = () => {
    const idLinea = linea?.documentId;
    if (!idLinea) return;
    navigate('/productos', {
      state: { selectedLineaId: idLinea, selectedSerie: serie },
    });
  };

  const handleImageClick = () => {
    if (!canClick) return;
    if (typeof window !== 'undefined') {
      TagManager.dataLayer({
        dataLayer: {
          event: 'ux_event',
          event_name: 'preview_image',
          event_category: 'engagement',
          model_name: nombre,
          value: 1
        }
      });
    }
    setPreviewOpen(true);
  };

  return (
    <motion.div
      className={styles.heroContainer}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div
        className={styles.heroContent}
        variants={containerVariants}
      >
        <BackgroundStroke count={3} />

        <motion.div
          className={styles.imageContainer}
          onClick={canClick ? handleImageClick : undefined}
          style={{ cursor: canClick ? 'pointer' : 'default' }}
          variants={imageVariants}
          initial="hidden"
          animate="visible"
        >
          <Suspense fallback={<div>Cargando imagen...</div>}>
            <LazyImage
              src={imagenCardUrl}
              alt={nombre}
              className={styles.image}
            />
          </Suspense>
        </motion.div>

        <motion.div
          className={styles.infoContainer}
          variants={fadeUp}
          initial="hidden"
          animate={infoControls}
        >
          <h3
            className={styles.serie}
            onClick={handleClickSerie}
            style={{ cursor: 'pointer' }}
          >
            {serieNombre}
          </h3>
          <h2 className={styles.title}>{nombre}</h2>

          <motion.div
            className={styles.buttonsContainer}
            variants={buttonsContainerVariants}
            initial="hidden"
            animate={buttonsControls}
          >
            <ModeloCotizacion modelo={modelo} onOpenCotizador={onOpenCotizador} />
            <ModeloFicha modelo={modelo} />
          </motion.div>
        </motion.div>
      </motion.div>

      {isPreviewOpen && (
        <ModelPreview
          src={imagenCardUrl}
          nombre={nombre}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </motion.div>
  );
};

export default ModeloHero;
